import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import { useAuth } from '../../../../modules/auth'
import { toast } from 'react-toastify'
import { useRealtimeStore } from '../../../store/realtimeStore'
import { useGetUser } from '../../../store/userStore'
import { isAdministrator, parseId } from '../../../helpers'
import axios from 'axios'
import { URL } from '../../../gql/client'

// GraphQL imports
import { CHAT_INBOX, CHAT_HISTORY, CHAT_TAGS, AVAILABLE_CHANNELS, INBOX_USERS } from '../../../gql/queries/chatQueries'
import { GET_ALL_ORGANIZATIONS } from '../../../gql/queries/organizationsQuery'
import {
  SEND_CHAT_MESSAGE,
  MARK_AS_READ,
  CREATE_TAG,
  MANAGE_CONTACT_TAG,
} from '../../../gql/mutations/chatMutations'
import { STORAGE_NAMES } from '../../../helpers/consts'

// Types (keep same as legacy)
export interface InboxUser {
  id: number
  full_name: string
  email: string
}

export interface ReceivedViaChannel {
  id: number
  provider: 'TWILIO' | 'BOTMAKER' | 'META'
  channel_type: 'SMS' | 'WHATSAPP' | 'VOICE'
  phone_number: string
  webhook_token: string
}

export interface Message {
  id: number
  direction: 'INBOUND' | 'OUTBOUND'
  content: string
  status: string
  created_at: string
  channel: string
  metadata?: any
  received_via_channel?: ReceivedViaChannel | null
}

export interface Tag {
  id: number
  name: string
  color: string
}

export interface ActiveChannelDetail {
  channel_type: string
  phone_number: string
}

export interface Conversation {
  id: number
  full_name: string
  phone: string
  avatar?: string
  last_message?: {
    content: string
    created_at: string
    direction: string
    channel?: string
    received_via_channel?: ReceivedViaChannel | null
  }
  unread_count: number
  assigned_to: number
  assigned_to_name?: string | null
  active_channels?: string[]
  active_channels_detail?: ActiveChannelDetail[]
  tags?: Tag[]
  config_source?: string
}

export interface ContactSearchResult {
  id: number
  full_name: string
  phone: string
  avatar?: string
  email?: string
}

export interface ConfigOption {
  id: number
  source: string
  label: string
  type: string
  whatsapp_number?: string
  sms_number?: string
}

export const useChatGraphQL = () => {
  const { auth } = useAuth()
  const authUser = useGetUser((s) => s.user)
  const isAdmin = isAdministrator(authUser?.permissions || [])
  const isSupervisor = authUser?.user?.primaryGroup?.name === 'Supervisor'
  const isSuperAdmin = (authUser?.groups ?? []).includes('SuperAdministrador')

  // State
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingInbox, setLoadingInbox] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)

  // Filters
  const [inboxMode, setInboxMode] = useState<'my' | 'general' | 'all'>('my')
  const [filterUnread, setFilterUnread] = useState(false)
  const [filterTags, setFilterTags] = useState<number[]>([])
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')
  const [filterSearch, setFilterSearch] = useState<string>('')
  const [filterUserId, setFilterUserId] = useState<number | null>(null)
  const [filterOrgId, setFilterOrgId] = useState<number | null>(null)
  const [pageSize] = useState(50)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalConversations, setTotalConversations] = useState(0)

  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [configSource, setConfigSource] = useState<string>('')
  const [availableConfigs, setAvailableConfigs] = useState<ConfigOption[]>([])
  const [inboxUsers, setInboxUsers] = useState<InboxUser[]>([])

  // GraphQL Queries
  const [fetchInboxQuery, { loading: loadingInboxQuery }] = useLazyQuery(CHAT_INBOX, {
    fetchPolicy: 'network-only',
  })

  const [fetchHistoryQuery, { loading: loadingHistoryQuery }] = useLazyQuery(CHAT_HISTORY, {
    fetchPolicy: 'network-only',
  })

  const { data: tagsData } = useQuery(CHAT_TAGS, {
    variables: isSuperAdmin && filterOrgId ? { organizationId: filterOrgId } : {},
    fetchPolicy: 'cache-and-network',
  })

  const { data: configsData } = useQuery(AVAILABLE_CHANNELS, {
    fetchPolicy: 'cache-first',
  })

  const [fetchInboxUsersQuery] = useLazyQuery(INBOX_USERS, {
    fetchPolicy: 'cache-first',
  })

  const [fetchOrgsQuery, { data: orgsData }] = useLazyQuery(GET_ALL_ORGANIZATIONS, {
    fetchPolicy: 'cache-first',
  })

  const orgOptions = useMemo(() => {
    const edges = orgsData?.allOrganizations?.edges ?? []
    return edges.map((e: any) => ({ value: Number(parseId(e.node.id)), label: e.node.name }))
  }, [orgsData])

  const fetchOrgOptions = useCallback(() => {
    if (isSuperAdmin) fetchOrgsQuery()
  }, [isSuperAdmin, fetchOrgsQuery])

  // GraphQL Mutations
  const [sendMessageMutation] = useMutation(SEND_CHAT_MESSAGE)
  const [markAsReadMutation] = useMutation(MARK_AS_READ)
  const [createTagMutation] = useMutation(CREATE_TAG)
  const [manageTagMutation] = useMutation(MANAGE_CONTACT_TAG)

  // Update state when tags data arrives
  useEffect(() => {
    if (tagsData?.chatTags) {
      setAvailableTags(tagsData.chatTags)
    }
  }, [tagsData])

  // Update state when configs data arrives
  useEffect(() => {
    if (configsData?.availableChannels) {
      setAvailableConfigs(
        configsData.availableChannels.map((c: any) => ({
          id: c.id,
          source: c.source,
          label: c.label,
          type: c.type,
          whatsapp_number: c.whatsappNumber,
          sms_number: c.smsNumber,
        }))
      )
    }
  }, [configsData])

  // Fetch inbox
  const fetchInbox = useCallback(
    async (modeOverride?: 'my' | 'general' | 'all', pageOverride?: number) => {
      const mode = modeOverride !== undefined ? modeOverride : inboxMode
      const page = pageOverride !== undefined ? pageOverride : currentPage
      setLoadingInbox(true)

      try {
        const result = await fetchInboxQuery({
          variables: {
            mode,
            unread: filterUnread || undefined,
            tags: filterTags.length > 0 ? filterTags : undefined,
            dateFrom: filterDateFrom || undefined,
            dateTo: filterDateTo || undefined,
            search: filterSearch.trim() || undefined,
            assignedUserId: filterUserId || undefined,
            organizationId: filterOrgId || undefined,
            offset: page * pageSize,
            limit: pageSize,
          },
        })

        if (result.data?.chatInbox) {
          const mapped = result.data.chatInbox.items.map((c: any) => ({
            id: c.id,
            full_name: c.fullName,
            phone: c.phone,
            avatar: c.avatar,
            last_message: c.lastMessage
              ? {
                  content: c.lastMessage.content,
                  created_at: c.lastMessage.createdAt,
                  direction: c.lastMessage.direction,
                  channel: c.lastMessage.channel,
                  received_via_channel: c.lastMessage.receivedViaChannel,
                }
              : undefined,
            unread_count: c.unreadCount,
            assigned_to: c.assignedTo,
            assigned_to_name: c.assignedToName,
            active_channels: c.activeChannels,
            active_channels_detail: c.activeChannelsDetail?.map((d: any) => ({
              channel_type: d.channelType,
              phone_number: d.phoneNumber
            })),
            tags: c.tags,
            config_source: c.configSource,
          }))

          setConversations(mapped)
          setTotalConversations(result.data.chatInbox.total)
          setConfigSource(result.data.chatInbox.configSource || '')
        }
      } catch (error) {
        console.error('Error fetching inbox:', error)
        toast.error('Error loading conversations')
      } finally {
        setLoadingInbox(false)
      }
    },
    [
      inboxMode,
      filterUnread,
      filterTags,
      filterDateFrom,
      filterDateTo,
      filterSearch,
      filterUserId,
      filterOrgId,
      currentPage,
      pageSize,
      fetchInboxQuery,
    ]
  )

  // Fetch messages
  const fetchMessages = useCallback(
    async (contactId: number) => {
      setLoadingMessages(true)

      try {
        const result = await fetchHistoryQuery({
          variables: { contactId },
        })

        if (result.data?.chatHistory) {
          const mapped = result.data.chatHistory.messages.map((m: any) => ({
            id: m.id,
            direction: m.direction,
            content: m.content,
            status: m.status,
            created_at: m.createdAt,
            channel: m.channel,
            metadata: m.metadata,
            received_via_channel: m.receivedViaChannel,
          }))

          setMessages(mapped)
          setConfigSource(result.data.chatHistory.configSource || '')
        }

        // Mark as read
        await markAsReadMutation({ variables: { contactId } })

        // Update unread count in conversations
        setConversations((prev) =>
          prev.map((c) => (c.id === contactId ? { ...c, unread_count: 0 } : c))
        )
      } catch (error) {
        console.error('Error fetching messages:', error)
        toast.error('Error loading messages')
      } finally {
        setLoadingMessages(false)
      }
    },
    [fetchHistoryQuery, markAsReadMutation]
  )

  // Send message
  const sendMessage = useCallback(
    async (
      contactId: number,
      content: string,
      channel: 'WHATSAPP' | 'SMS' = 'WHATSAPP',
      configId?: number
    ) => {
      try {
        const result = await sendMessageMutation({
          variables: {
            contactId,
            content,
            channel,
            configId,
          },
        })

        if (result.data?.sendChatMessage?.success) {
          const newMsg: Message = {
            id: result.data.sendChatMessage.messageId,
            direction: 'OUTBOUND',
            content,
            status: 'SENT',
            created_at: result.data.sendChatMessage.createdAt,
            channel,
          }

          setMessages((prev) => [...prev, newMsg])

          // Update conversation list
          setConversations((prev) => {
            const updated = prev.map((c) =>
              c.id === contactId
                ? {
                    ...c,
                    last_message: {
                      content,
                      created_at: newMsg.created_at,
                      direction: 'OUTBOUND',
                      channel,
                    },
                  }
                : c
            )
            const target = updated.find((c) => c.id === contactId)
            const others = updated.filter((c) => c.id !== contactId)
            return target ? [target, ...others] : others
          })
        } else {
          toast.error(result.data?.sendChatMessage?.error || 'Error sending message')
        }
      } catch (error) {
        console.error('Error sending message:', error)
        toast.error('Error sending message')
      }
    },
    [sendMessageMutation]
  )

  // Send message with media (keep as REST for now)
  const sendMessageWithMedia = useCallback(
    async (
      contactId: number,
      content: string,
      channel: 'SMS' | 'WHATSAPP',
      file: File,
      configId?: number
    ) => {
      const token = auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
      if (!token) return

      try {
        const formData = new FormData()
        formData.append('contact_id', String(contactId))
        formData.append('content', content)
        formData.append('channel', channel)
        formData.append('file', file, file.name)
        if (configId) formData.append('config_id', String(configId))

        const response = await axios.post(`${URL}/api/chat/send-media`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        })

        const newMsg: Message = {
          id: response.data.message_id,
          direction: 'OUTBOUND',
          content: content || `[MMS: ${file.name}]`,
          status: 'SENT',
          created_at: response.data.created_at,
          channel,
          metadata: {
            media_urls: [response.data.media_url],
          },
        }

        setMessages((prev) => [...prev, newMsg])

        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.id === contactId
              ? {
                  ...c,
                  last_message: {
                    content: content || '[MMS]',
                    created_at: newMsg.created_at,
                    direction: 'OUTBOUND',
                    channel,
                  },
                }
              : c
          )
          const target = updated.find((c) => c.id === contactId)
          const others = updated.filter((c) => c.id !== contactId)
          return target ? [target, ...others] : others
        })
      } catch (error) {
        console.error('Error sending media:', error)
        toast.error('Error sending message')
      }
    },
    [auth?.api_token]
  )

  // Create tag
  const createTag = useCallback(
    async (name: string, color: string) => {
      try {
        const result = await createTagMutation({
          variables: { name, color },
        })

        if (result.data?.createTag?.tag) {
          const newTag = result.data.createTag.tag
          setAvailableTags((prev) => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)))
          return newTag
        }
      } catch (error) {
        console.error('Error creating tag:', error)
        throw error
      }
    },
    [createTagMutation]
  )

  // Manage tag
  const manageTag = useCallback(
    async (contactId: number, tagId: number, action: 'add' | 'remove') => {
      try {
        const result = await manageTagMutation({
          variables: { contactId, tagId, action },
        })

        if (result.data?.manageContactTag?.tags) {
          setConversations((prev) =>
            prev.map((c) =>
              c.id === contactId ? { ...c, tags: result.data.manageContactTag.tags } : c
            )
          )

          if (currentConversation?.id === contactId) {
            setCurrentConversation((prev) =>
              prev ? { ...prev, tags: result.data.manageContactTag.tags } : null
            )
          }
        }
      } catch (error) {
        console.error('Error managing tag:', error)
        throw error
      }
    },
    [manageTagMutation, currentConversation?.id]
  )

  // Search contacts (use REST for now, can migrate to GraphQL later)
  const searchContacts = useCallback(
    async (query: string) => {
      const token = auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
      if (!token) return []

      try {
        const response = await axios.get(`${URL}/api/chat/contacts?q=${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        return response.data.items as ContactSearchResult[]
      } catch (error) {
        console.error('Error searching contacts:', error)
        return []
      }
    },
    [auth?.api_token]
  )

  // Start conversation
  const startConversation = useCallback((contact: ContactSearchResult) => {
    const existing = conversations.find((c) => c.id === contact.id)
    if (existing) {
      setCurrentConversation(existing)
    } else {
      const newConv: Conversation = {
        id: contact.id,
        full_name: contact.full_name,
        phone: contact.phone,
        avatar: contact.avatar,
        unread_count: 0,
        assigned_to: 0,
      }
      setConversations((prev) => [newConv, ...prev])
      setCurrentConversation(newConv)
    }
  }, [conversations])

  // Fetch inbox users — accepts optional orgId for superadmin cross-org filtering
  const fetchInboxUsersCallback = useCallback(async (orgId?: number | null) => {
    try {
      const result = await fetchInboxUsersQuery({
        variables: orgId ? { organizationId: orgId } : {},
        fetchPolicy: 'network-only',
      })
      if (result.data?.inboxUsers) {
        setInboxUsers(
          result.data.inboxUsers.map((u: any) => ({
            id: u.id,
            full_name: u.fullName,
            email: u.email,
          }))
        )
      }
    } catch (error) {
      console.error('Error fetching inbox users:', error)
    }
  }, [fetchInboxUsersQuery])

  // When superadmin changes org filter, reload users/tags for that org and clear dependent filters
  useEffect(() => {
    if (isSuperAdmin) {
      setFilterUserId(null)
      setFilterTags([])
      setInboxUsers([])
      fetchInboxUsersCallback(filterOrgId)
    }
  }, [filterOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket handler
  const handleNewMessageRef = useRef<(msg: any) => void>(() => {})

  useEffect(() => {
    handleNewMessageRef.current = (msg: any) => {
      // Los eventos NEW_MESSAGE también se emiten para mensajes OUTBOUND (respuesta
      // manual del agente o respuesta automática de Dify) para que otras pestañas/
      // dispositivos del mismo agente vean la conversación actualizada. Pero el propio
      // remitente ya actualizó su estado local de forma optimista al enviar (ver
      // sendMessage más arriba) — no hay que contarlo como no leído ni duplicarlo.
      const direction = msg.direction || 'INBOUND'
      const isOutbound = direction === 'OUTBOUND'

      setConversations((prev) => {
        const exists = prev.find((c) => c.id === msg.contact_id)

        let updatedList = [...prev]

        if (exists) {
          updatedList = updatedList.map((c) =>
            c.id === msg.contact_id
              ? {
                  ...c,
                  unread_count: isOutbound
                    ? c.unread_count
                    : currentConversation?.id === msg.contact_id
                    ? 0
                    : c.unread_count + 1,
                  last_message: {
                    content: msg.content,
                    created_at: msg.created_at,
                    direction,
                    channel: msg.channel,
                  },
                }
              : c
          )
        } else {
          if (isOutbound) return prev
          fetchInbox()
          return prev
        }

        const target = updatedList.find((c) => c.id === msg.contact_id)
        const others = updatedList.filter((c) => c.id !== msg.contact_id)
        return target ? [target, ...others] : others
      })

      if (isOutbound) return

      const isCurrent =
        currentConversation && String(currentConversation.id) === String(msg.contact_id)

      if (isCurrent) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.communication_id)) return prev
          return [
            ...prev,
            {
              id: msg.communication_id,
              direction: 'INBOUND',
              content: msg.content,
              status: 'RECEIVED',
              created_at: msg.created_at,
              channel: msg.channel || '',
              metadata: msg.metadata || {},
            },
          ]
        })
      }
    }
  })

  // Listen to WebSocket
  const lastEventType = useRealtimeStore((state) => state.lastEventType)
  const lastEventPayload = useRealtimeStore((state) => state.lastEventPayload)
  const lastEventTimestamp = useRealtimeStore((state) => state.lastEventTimestamp)

  useEffect(() => {
    if (lastEventType === 'NEW_MESSAGE' && lastEventPayload) {
      handleNewMessageRef.current(lastEventPayload)
    }
  }, [lastEventType, lastEventPayload, lastEventTimestamp])

  // Effect: fetch messages when selecting conversation
  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation.id)
    } else {
      setMessages([])
    }
  }, [currentConversation, fetchMessages])

  // Effect: reset page when mode or filters change
  useEffect(() => {
    setCurrentPage(0)
  }, [inboxMode, filterUnread, filterTags, filterDateFrom, filterDateTo, filterSearch, filterUserId, filterOrgId])

  // Effect: fetch inbox when mode, filters, or page changes
  useEffect(() => {
    fetchInbox()
  }, [currentPage, inboxMode, filterUnread, filterTags, filterDateFrom, filterDateTo, filterSearch, filterUserId, filterOrgId])

  return {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loadingInbox: loadingInbox || loadingInboxQuery,
    loadingMessages: loadingMessages || loadingHistoryQuery,
    sendMessage,
    sendMessageWithMedia,
    refreshInbox: fetchInbox,
    searchContacts,
    startConversation,

    // Helpers
    reloadInbox: () => fetchInbox(),

    // Filters
    inboxMode,
    setInboxMode,
    isAdmin,
    isSupervisor,
    isSuperAdmin,
    filterUnread,
    setFilterUnread,
    filterTags,
    setFilterTags,
    filterDateFrom,
    setFilterDateFrom,
    filterDateTo,
    setFilterDateTo,
    filterSearch,
    setFilterSearch,
    filterUserId,
    setFilterUserId,
    filterOrgId,
    setFilterOrgId,
    orgOptions,
    fetchOrgOptions,
    availableTags,
    createTag,
    manageTag,
    configSource,
    availableConfigs,
    inboxUsers,
    fetchInboxUsers: fetchInboxUsersCallback,

    // Pagination
    pageSize,
    currentPage,
    setCurrentPage,
    totalConversations,
  }
}
